import React, { useState, useMemo } from 'react';
import ListItem from '@mui/material/ListItem';
import { useLocation, matchPath } from 'react-router';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import { Box, useTheme, Theme, Tooltip, Collapse } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { NavRoute } from '~/layouts/Admin/RoutePermissions';
import useWindowSize from '~/hooks/useWindowSize';
import SidebarItemIcon from '../ItemIcon';
import { CustomNavLink } from '../../Link/Link';
import { SidebarRoute } from '../utils';
import {
  getMobileLinkStyle,
  getMobileNestedLinkStyle,
  getMobileTextStyle,
  getMobileCaretStyle,
  getItemLinkStyles,
  getCollapseItemLinkStyles,
  getItemTextStyles,
  getCollapseItemTextStyles,
  getCaretStyles,
  getListItemStyle,
} from './styles';
import NavFlyout from '../NavFlyout';

type StyleObject = SystemStyleObject<Theme>;

/**
 * check if a route is a NavRoute (has paramsToKeep property).
 */
function isNavRoute(route: SidebarRoute): route is NavRoute {
  return 'paramsToKeep' in route || 'perm' in route;
}

/**
 * extract paramsToKeep from a route if it's a NavRoute.
 */
function getParamsToKeep(route: SidebarRoute): string[] | undefined {
  if (isNavRoute(route)) {
    return route.paramsToKeep;
  }
  return undefined;
}

/**
 * check if a route is an external link.
 * external routes have an `external: true` property (not in base types, added at runtime).
 */
function isExternalRoute(route: SidebarRoute): boolean {
  return 'external' in route && (route as { external?: boolean }).external === true;
}

/**
 * Manages collapse state for sidebar links.
 *
 * In regular sidebar: collapse is controlled by parent via `isCollapseActive`
 * In flyout context: collapse is controlled locally via internal state
 *
 * This hook unifies both read (isOpen) and write (handleClick) into a single abstraction.
 */
function useCollapseControl(
  isCollapseActive: boolean,
  inFlyout: boolean,
  hasCaret: boolean,
  onNavLinkClick?: (e: React.MouseEvent) => void,
) {
  const [localOpen, setLocalOpen] = useState(false);

  // unified read: flyout uses local state, regular sidebar uses parent prop
  const isOpen = inFlyout ? localOpen : isCollapseActive;

  // unified write: flyout toggles local state, regular sidebar delegates to parent
  const handleClick = (e: React.MouseEvent) => {
    if (inFlyout && hasCaret) {
      e.preventDefault();
      setLocalOpen((prev) => !prev);
      return;
    }
    onNavLinkClick?.(e);
  };

  return { isOpen, handleClick };
}

function keepParams(paramsToKeep: string[] | undefined, searchString: string) {
  if (!paramsToKeep || !searchString) return '';
  const searchParams = new URLSearchParams(searchString);
  const newSearchParams = new URLSearchParams();
  paramsToKeep.forEach((param) => {
    searchParams.getAll(param).forEach((value) => {
      newSearchParams.append(param, value);
    });
  });
  return `?${newSearchParams.toString()}`;
}

/**
 * Determines if a route is active using React Router's matchPath.
 *
 * for routes with children (collapse routes), uses prefix matching
 * so /analytics matches /analytics/anything.
 *
 * for leaf routes (no children), uses strict matching.
 */
function activeRoute(
  routePath: string,
  basePath: string,
  hasChildren: boolean,
  locationPathname: string,
) {
  if (!routePath) return false;
  const fullRoutePath = `${basePath}${routePath.startsWith('/') ? routePath : `/${routePath}`}`;

  if (hasChildren) {
    const match = matchPath({ path: fullRoutePath, end: false }, locationPathname);
    return !!match;
  }

  const match = matchPath({ path: fullRoutePath, end: true }, locationPathname);
  return !!match;
}

interface LinkContentProps {
  name: string;
  hasCaret: boolean;
  icon?: string;
  active: boolean;
  sidebarOpen: boolean;
}

interface MobileLinkContentProps extends LinkContentProps {
  hasIcon: boolean;
  textStyles: { fontSize: string; fontWeight: number; color: string };
  caretStyle: StyleObject;
}

function MobileLinkContent({
  name,
  hasIcon,
  hasCaret,
  icon,
  active,
  sidebarOpen,
  textStyles,
  caretStyle,
}: MobileLinkContentProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
      {hasIcon && (
        <ListItemIcon sx={{ minWidth: 'auto', mr: 1.5, color: 'inherit' }}>
          <SidebarItemIcon icon={icon} active={active} hover={false} sidebarOpen={sidebarOpen} />
        </ListItemIcon>
      )}
      <ListItemText primary={name} slotProps={{ primary: { sx: textStyles } }} />
      {hasCaret && <Box component="span" sx={caretStyle} />}
    </Box>
  );
}

interface DesktopLinkContentProps extends LinkContentProps {
  hover: boolean;
  textStyles: StyleObject;
  caretStyles: StyleObject;
}

function DesktopLinkContent({
  name,
  hasCaret,
  icon,
  active,
  hover,
  sidebarOpen,
  textStyles,
  caretStyles,
}: DesktopLinkContentProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <SidebarItemIcon icon={icon} active={active} hover={hover} sidebarOpen={sidebarOpen} />
      <ListItemText
        primary={name}
        secondary={hasCaret ? <Box component="b" sx={caretStyles} /> : undefined}
        disableTypography
        sx={textStyles}
      />
    </Box>
  );
}

interface LinkWrapperProps {
  children: React.ReactNode;
  linkStyles: StyleObject;
  hasCaret: boolean;
  isExternal: boolean;
  path: string;
  basePath: string;
  paramsToKeep: string[] | undefined;
  locationSearch: string;
  onClick: (e: React.MouseEvent) => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

function CollapseButton({
  children,
  linkStyles,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: Pick<
  LinkWrapperProps,
  'children' | 'linkStyles' | 'onClick' | 'onMouseEnter' | 'onMouseLeave'
>) {
  return (
    <Box
      role="button"
      tabIndex={0}
      sx={linkStyles}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </Box>
  );
}

function ExternalLinkWrapper({
  children,
  linkStyles,
  path,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: Pick<
  LinkWrapperProps,
  'children' | 'linkStyles' | 'path' | 'onClick' | 'onMouseEnter' | 'onMouseLeave'
>) {
  return (
    <Box
      component="a"
      href={path}
      target="_blank"
      rel="noopener noreferrer"
      sx={linkStyles}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </Box>
  );
}

function InternalLinkWrapper({
  children,
  linkStyles,
  path,
  basePath,
  paramsToKeep,
  locationSearch,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: Omit<LinkWrapperProps, 'hasCaret' | 'isExternal'>) {
  return (
    <Box sx={{ margin: 0, padding: 0, '& a': linkStyles }}>
      <CustomNavLink
        to={`${basePath}${path}${keepParams(paramsToKeep, locationSearch)}`}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {children}
      </CustomNavLink>
    </Box>
  );
}

interface LinkProps {
  children?: React.ReactNode;
  flyoutContent?: React.ReactNode; // collapsed child popout menu content
  hasCaret: boolean;
  isCollapseActive: boolean;
  prop: SidebarRoute;
  sidebarOpen: boolean;
  onNavLinkClick: (e: React.MouseEvent) => void;
  level: number;
  basePath: string;
  activeFlyoutPath?: string | null; // which flyout is currently open (by path)
  onFlyoutChange?: (path: string | null) => void; // callback to change active flyout
  inFlyout?: boolean; // true when rendered inside a flyout popover
  isNavDataLoading?: boolean; // true when nav data (analytics/insights) is still loading
}

function Link({
  children,
  flyoutContent,
  hasCaret,
  isCollapseActive = false,
  prop,
  sidebarOpen,
  onNavLinkClick,
  level = 0,
  basePath = '',
  activeFlyoutPath,
  onFlyoutChange,
  inFlyout = false,
  isNavDataLoading = false,
}: LinkProps) {
  const theme = useTheme();
  const location = useLocation();
  const { isMobile } = useWindowSize();

  // hasCaret indicates this is a parent link with children
  const active = activeRoute(prop.path, basePath, hasCaret, location.pathname);
  const hasIcon = !!prop.icon;
  const paramsToKeep = getParamsToKeep(prop);
  const isExternal = isExternalRoute(prop);

  const [hover, setHover] = useState(false);
  const [linkHover, setLinkHover] = useState(false); // Tracks hover on link only (not children)
  const [anchorEl, setAnchorEl] = useState<HTMLLIElement | null>(null);
  const [flyoutHover, setFlyoutHover] = useState(false);

  // unified collapse state management (local for flyout, parent-controlled for regular sidebar)
  const { isOpen: isCollapseOpen, handleClick } = useCollapseControl(
    isCollapseActive,
    inFlyout,
    hasCaret,
    onNavLinkClick,
  );

  const isThisFlyoutActive = activeFlyoutPath === prop.path;

  /**
   * Determines if the flyout popover should be visible.
   * Flyouts only appear for collapsed sidebar on desktop, for parent items with children.
   */
  const flyoutOpen = useMemo(() => {
    // flyouts only work on collapsed desktop sidebar
    if (sidebarOpen || isMobile) return false;
    // only parent items (with children) have flyouts
    if (!hasCaret) return false;
    // nested items inside a flyout expand inline, not as new poppers
    if (inFlyout) return false;
    // need an anchor element to position the popper
    if (!anchorEl) return false;
    // only one flyout open at a time - must be this one
    if (!isThisFlyoutActive) return false;
    // show while hovering either the trigger or the flyout panel
    return hover || flyoutHover;
  }, [sidebarOpen, isMobile, hasCaret, inFlyout, anchorEl, isThisFlyoutActive, hover, flyoutHover]);

  const handleMouseEnter = () => {
    setHover(true);
    setLinkHover(true);
  };

  const handleMouseLeave = () => {
    setHover(false);
    setLinkHover(false);
  };

  const handleListItemMouseEnter = (e: React.MouseEvent<HTMLLIElement>) => {
    // Only manage flyout state for top-level items (not inside a flyout)
    if (!sidebarOpen && hasCaret && !isMobile && !inFlyout) {
      setAnchorEl(e.currentTarget);
      onFlyoutChange?.(prop.path);
    }
    setHover(true);
  };

  const handleListItemMouseLeave = () => {
    setHover(false);
  };

  const handleFlyoutMouseEnter = () => {
    setFlyoutHover(true);
  };

  const handleFlyoutMouseLeave = () => {
    setFlyoutHover(false);
    // Clear active flyout when leaving the flyout panel
    if (isThisFlyoutActive) {
      onFlyoutChange?.(null);
    }
  };

  const isPublisher = basePath.startsWith('/partner/');
  // When flyout is open in collapsed mode, treat parent as active for styling
  const effectiveActive = active || flyoutOpen;
  const linkStyleParams = {
    sidebarOpen,
    active: effectiveActive,
    hasChildren: hasCaret,
    theme,
    isPublisher,
  };
  const textStyleParams = { sidebarOpen, active: effectiveActive, theme };

  const getMobileLinkStyles = () => {
    if (level === 0) return getMobileLinkStyle();
    return getMobileNestedLinkStyle();
  };

  const getDesktopLinkStyles = () => {
    if (hasIcon) return getItemLinkStyles(linkStyleParams);
    return getCollapseItemLinkStyles(linkStyleParams);
  };

  const linkStyles = isMobile ? getMobileLinkStyles() : getDesktopLinkStyles();

  const mobileTextStyles = getMobileTextStyle({ level, active, theme });
  const desktopTextStyles = hasIcon
    ? getItemTextStyles(textStyleParams)
    : getCollapseItemTextStyles(textStyleParams);

  const caretStyles = getCaretStyles({ level, isCollapseActive: isCollapseOpen });
  const mobileCaretStyle = getMobileCaretStyle({ isCollapseActive: isCollapseOpen });
  const listItemStyle = getListItemStyle({ isMobile, isChild: !hasIcon, theme });

  // extract icon as string:
  // - string → use it
  // - undefined/null → no icon
  // - other (e.g. MUI component) → fallback to 'Overview'
  const getIconName = (): string | undefined => {
    if (typeof prop.icon === 'string') return prop.icon;
    if (prop.icon) return 'Overview';
    return undefined;
  };
  const icon = getIconName();

  // render content based on mobile/desktop
  const linkContent = isMobile ? (
    <MobileLinkContent
      name={prop.name}
      hasIcon={hasIcon}
      hasCaret={hasCaret}
      icon={icon}
      active={active}
      sidebarOpen={sidebarOpen}
      textStyles={mobileTextStyles}
      caretStyle={mobileCaretStyle}
    />
  ) : (
    <DesktopLinkContent
      name={prop.name}
      hasCaret={hasCaret}
      icon={icon}
      active={effectiveActive}
      sidebarOpen={sidebarOpen}
      hover={linkHover}
      textStyles={desktopTextStyles}
      caretStyles={caretStyles}
    />
  );

  // render appropriate link wrapper
  const renderLink = () => {
    if (hasCaret) {
      return (
        <CollapseButton
          linkStyles={linkStyles}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {linkContent}
        </CollapseButton>
      );
    }

    if (isExternal) {
      return (
        <ExternalLinkWrapper
          linkStyles={linkStyles}
          path={prop.path}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {linkContent}
        </ExternalLinkWrapper>
      );
    }

    return (
      <InternalLinkWrapper
        linkStyles={linkStyles}
        path={prop.path}
        basePath={basePath}
        paramsToKeep={paramsToKeep}
        locationSearch={location.search}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {linkContent}
      </InternalLinkWrapper>
    );
  };

  /**
   * Children rendering strategy:
   * - `children`: Direct React children for inline sidebar display (expanded sidebar or mobile)
   * - `flyoutContent`: Pre-rendered children passed separately for flyout popper display
   *
   * When sidebar is expanded or on mobile: show children inline within the ListItem
   * When sidebar is collapsed on desktop: show flyoutContent in a Popper
   * When inside a flyout: nested items with children expand inline (no nested poppers)
   */
  const showInlineChildren = sidebarOpen || isMobile || inFlyout;
  const showPopper = flyoutOpen;

  // show tooltip when sidebar is collapsed (desktop only) for top-level links
  // disable tooltip when popper is open to avoid conflicts
  const isTopLevel = level === 0;
  const showTooltip = !sidebarOpen && !isMobile && isTopLevel && !flyoutOpen;

  return (
    <>
      <ListItem
        sx={listItemStyle}
        onMouseEnter={handleListItemMouseEnter}
        onMouseLeave={handleListItemMouseLeave}
      >
        {showTooltip ? (
          <Tooltip title={prop.name} placement="right" arrow>
            <Box component="span" sx={{ display: 'block' }}>
              {renderLink()}
            </Box>
          </Tooltip>
        ) : (
          renderLink()
        )}
        {/* inline children: displayed within the ListItem when sidebar is expanded/mobile */}
        {showInlineChildren && (
          <>
            {/* nested items inside flyout: use flyoutContent with collapse control */}
            {inFlyout && hasCaret && flyoutContent && (
              <Collapse in={isCollapseOpen} unmountOnExit>
                {flyoutContent}
              </Collapse>
            )}
            {/* regular sidebar: render children directly */}
            {!inFlyout && children}
          </>
        )}
      </ListItem>

      {hasCaret && !isMobile && !inFlyout && (
        <NavFlyout
          open={showPopper}
          anchorEl={anchorEl}
          title={prop.name}
          onMouseEnter={handleFlyoutMouseEnter}
          onMouseLeave={handleFlyoutMouseLeave}
          isLoading={isNavDataLoading}
        >
          {flyoutContent || children}
        </NavFlyout>
      )}
    </>
  );
}

export default Link;
